import React, { useEffect, useMemo, useState } from 'react';
import { colors, getAvatarColor, statusColors } from '../utils/colors';
import { getAttendance, type AttendanceRecord } from '../Api/auth';
import type { TeacherListItem } from '../utils/types';
import { formatMarkedTime } from '../utils/time';

interface Props {
  teacher: TeacherListItem;
  onClose: () => void;
  onUpdate: () => void;
}

function pad2(n: number) {
  return n.toString().padStart(2, '0');
}

const TITLES = new Set([
  'mr', 'mrs', 'ms', 'miss', 'mx',
  'dr', 'prof', 'sir', 'madam', 'mam',
  'smt', 'shri', 'sri', 'shree',
]);

function initials(name: string) {
  const words = name
    .split(/\s+/)
    .map(w => w.replace(/\.$/, ''))
    .filter(w => w && !TITLES.has(w.toLowerCase()));
  return words.slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

function formatDay(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric', weekday: 'short' });
}

const AlreadyMarkedDrawer: React.FC<Props> = ({ teacher, onClose, onUpdate }) => {
  const [records, setRecords] = useState<AttendanceRecord[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setRecords(null);
    getAttendance({ teacher_id: teacher.id })
      .then(rs => { if (!cancelled) setRecords(rs); })
      .catch(err => { if (!cancelled) setError(err?.message ?? 'Failed to load attendance.'); });
    return () => { cancelled = true; };
  }, [teacher.id]);

  const now = new Date();
  const todayISO = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
  const todayRecord = useMemo(() => {
    if (!records) return null;
    return records.find(r => (r.date ?? '').slice(0, 10) === todayISO) ?? null;
  }, [records, todayISO]);

  const headerStatus = teacher.todayStatus ?? 'present';
  const sc = statusColors[headerStatus];

  const punctualityLabel =
    todayRecord?.status === 'present' ? 'On Time' :
    todayRecord?.status === 'late'    ? 'Late'    :
    todayRecord?.status === 'absent'  ? 'Absent'  : '';
  const recordSc = todayRecord ? statusColors[todayRecord.status] : null;
  const markedTime = todayRecord ? formatMarkedTime(todayRecord.marked_at_device, todayRecord.created_at) : '';

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 200, backdropFilter: 'blur(2px)' }}
      />
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        background: '#fff', borderRadius: '24px 24px 0 0',
        zIndex: 201, paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
        animation: 'sheetSlideUp 0.32s cubic-bezier(0.22,1,0.36,1) both',
        fontFamily: "'Inter', sans-serif",
      }}>
        {/* Handle */}
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 12, paddingBottom: 4 }}>
          <div style={{ width: 40, height: 4, background: colors.border, borderRadius: 4 }} />
        </div>

        {/* Teacher header pill */}
        <div style={{
          margin: '10px 20px 18px',
          background: sc.bg,
          border: `1px solid ${sc.border}`,
          borderRadius: 16, padding: '12px 14px',
          display: 'flex', alignItems: 'center', gap: 14,
        }}>
          <div style={{
            width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
            background: getAvatarColor(teacher.name),
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 15, fontWeight: 800, color: '#fff',
          }}>
            {initials(teacher.name)}
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: colors.text, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {teacher.name}
          </div>
        </div>

        {/* Read-only today's marked details */}
        <div style={{ margin: '0 20px 14px' }}>
         
          <div style={{
            border: `1px solid ${colors.border}`, borderRadius: 12,
            background: '#fff', padding: '14px 14px',
          }}>
            {records == null ? (
              <div style={{ textAlign: 'center', fontSize: 13, color: colors.textMuted, padding: '6px 0' }}>
                Loading…
              </div>
            ) : !todayRecord ? (
              <div style={{ textAlign: 'center', fontSize: 13, color: colors.textMuted, padding: '6px 0' }}>
                Not marked yet today.
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: colors.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {formatDay(todayRecord.date)}
                  </span>
                  {markedTime && (
                    <>
                      <span style={{ color: colors.border }}>•</span>
                      <span style={{ fontSize: 12, color: colors.textMuted, whiteSpace: 'nowrap' }}>{markedTime}</span>
                    </>
                  )}
                </div>
                {recordSc && (
                  <div style={{
                    fontSize: 11, fontWeight: 700, letterSpacing: '0.4px',
                    color: recordSc.text, background: recordSc.bg,
                    border: `1px solid ${recordSc.border}`,
                    borderRadius: 999, padding: '3px 10px',
                    flexShrink: 0,
                  }}>
                    {punctualityLabel}
                  </div>
                )}
              </div>
            )}
            {error && (
              <div style={{ marginTop: 8, textAlign: 'center', fontSize: 12, color: colors.danger }}>{error}</div>
            )}
          </div>
        </div>

       
      </div>
    </>
  );
};

export default AlreadyMarkedDrawer;
